import { useCallback } from "react";
import { useIntl } from "react-intl";

import useLazyQueryWithPromise from "@hooks/useLazyQueryWithPromise";
import {
  CarePlanActionItemCtaEnum,
  GetWorksheetTemplatesSearchDocument,
  GetWorksheetTemplatesSearchQuery,
  GetWorksheetTemplatesSearchQueryVariables
} from "@typing/Generated";

const useSearchWorksheetTemplates = (
  includeTools = false,
  onClick: (id: string, title: string) => void,
  onToolAdd?: (tool: CarePlanActionItemCtaEnum) => void
) => {
  const intl = useIntl();
  const getWorksheetTemplatesSearch = useLazyQueryWithPromise<
    GetWorksheetTemplatesSearchQuery,
    GetWorksheetTemplatesSearchQueryVariables
  >(GetWorksheetTemplatesSearchDocument);

  const searchOptions = useCallback(
    (v: string) =>
      getWorksheetTemplatesSearch({
        variables: {
          count: 35,
          search: v
        }
      }).then(res => {
        const nodes = (res.data?.worksheetTemplatesSearch ?? []).map(node => ({
          category: "",
          highlight: node.highlight,
          label: node.title,
          onClick: () => onClick(node.id, node.title),
          value: node.id
        }));

        const trackerOptions = [
          CarePlanActionItemCtaEnum.COMPLETE_LEGAL_DOCUMENT_TRACKER,
          CarePlanActionItemCtaEnum.COMPLETE_MEDICATION_TRACKER,
          CarePlanActionItemCtaEnum.COMPLETE_PROVIDER_LIST
        ]
          .map(option => ({
            category: intl.formatMessage({ id: "pages.carePlan.base.tools.trackers" }),
            label: intl.formatMessage({ id: `models.tracker.typeCtaOptions.${option}` }),
            onClick: () => onToolAdd?.(option),
            value: option
          }))
          .filter(option => option.label.toLowerCase().includes(v.toLowerCase()));

        if (includeTools && trackerOptions.length) {
          return [
            { label: intl.formatMessage({ id: "pages.carePlan.base.tools.worksheets" }), options: nodes },
            {
              label: intl.formatMessage({ id: "pages.carePlan.base.tools.trackers" }),
              options: trackerOptions
            }
          ];
        }

        return [
          {
            label: "",
            options: nodes
          }
        ];
      }),
    [getWorksheetTemplatesSearch, includeTools, intl, onClick, onToolAdd]
  );

  return searchOptions;
};

export default useSearchWorksheetTemplates;
